/**
 * Admin — Franchise Manager (Master, DDP, Signature stores, Signature FICO).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImageIcon, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { uploadHref } from '../../utils/uploadHref';

const TABS = [
    {
        id: 'master',
        label: 'Master franchise',
        title: 'Manage Master Franchises',
        fields: [{ key: 'territory', label: 'Territory name', required: true, placeholder: 'e.g., Delhi NCR, Telangana' }],
    },
    {
        id: 'ddp',
        label: 'DDP franchise',
        title: 'Manage DDP Franchises',
        fields: [
            { key: 'state', label: 'State', required: true, placeholder: 'e.g., Odisha' },
            { key: 'ddpTerritory', label: 'Territory (districts)', required: true, placeholder: 'e.g., Cuttack, Jagatsinghpur', textarea: true },
        ],
    },
    {
        id: 'signature',
        label: 'Signature stores',
        title: 'Manage Signature Stores',
        fields: [
            { key: 'state', label: 'State', required: true, placeholder: 'e.g., Rajasthan' },
            { key: 'district', label: 'District', required: true, placeholder: 'e.g., Jaipur' },
            { key: 'city', label: 'City', required: true, placeholder: 'e.g., Jaipur' },
            { key: 'pin', label: 'PIN code', required: true, placeholder: 'e.g., 302001' },
        ],
    },
    {
        id: 'signatureFico',
        label: 'Signature FICO',
        title: 'Manage Signature Fico',
        fields: [
            { key: 'state', label: 'State', required: true, placeholder: 'e.g., Maharashtra' },
            { key: 'district', label: 'District', required: true, placeholder: 'e.g., Mumbai Suburban' },
            { key: 'city', label: 'City', required: true, placeholder: 'e.g., Mumbai' },
            { key: 'pin', label: 'PIN code', required: true, placeholder: 'e.g., 400001' },
        ],
    },
];

const STATUSES = ['Available', 'Booked', 'Blocked'];

const STATUS_STYLES = {
    Available: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    Booked: 'bg-amber-50 text-amber-800 ring-amber-200',
    Blocked: 'bg-rose-50 text-rose-700 ring-rose-200',
};

function blankForm(type) {
    return {
        type,
        territory: '',
        state: '',
        ddpTerritory: '',
        district: '',
        city: '',
        pin: '',
        status: 'Available',
        image: '',
        description: '',
    };
}

export default function FranchiseManager() {
    const [tab, setTab] = useState('master');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [form, setForm] = useState(blankForm('master'));
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const activeTab = useMemo(() => TABS.find((t) => t.id === tab) || TABS[0], [tab]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { type: tab };
            if (statusFilter) params.status = statusFilter;
            if (search.trim()) params.q = search.trim();
            const { data } = await api.get('/franchises/admin', { params });
            setItems(data.items || []);
        } catch {
            /* toasted */
        } finally {
            setLoading(false);
        }
    }, [tab, statusFilter, search]);

    useEffect(() => {
        setForm(blankForm(tab));
        setEditingId(null);
    }, [tab]);

    useEffect(() => {
        const t = window.setTimeout(load, search ? 300 : 0);
        return () => window.clearTimeout(t);
    }, [load, search]);

    const uploadImage = async (file) => {
        if (!file) return;
        if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
            toast.error('Use JPEG, PNG, WebP or GIF');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('photo', file);
            const { data } = await api.post('/franchises/upload-image', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setForm((prev) => ({ ...prev, image: data.image }));
            toast.success('Image uploaded');
        } finally {
            setUploading(false);
        }
    };

    const startEdit = (item) => {
        setEditingId(item._id);
        setForm({
            type: item.type,
            territory: item.territory || '',
            state: item.state || '',
            ddpTerritory: item.ddpTerritory || '',
            district: item.district || '',
            city: item.city || '',
            pin: item.pin || '',
            status: item.status || 'Available',
            image: item.image || '',
            description: item.description || '',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetForm = () => {
        setEditingId(null);
        setForm(blankForm(tab));
        if (fileRef.current) fileRef.current.value = '';
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form, type: tab };
            if (editingId) {
                await api.patch(`/franchises/${editingId}`, payload);
                toast.success('Franchise updated');
            } else {
                await api.post('/franchises', payload);
                toast.success('Franchise added');
            }
            resetForm();
            load();
        } finally {
            setSaving(false);
        }
    };

    const remove = async (item) => {
        if (!confirm(`Delete "${item.displayName}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/franchises/${item._id}`);
            toast.success('Deleted');
            if (editingId === item._id) resetForm();
            load();
        } catch {
            /* toasted */
        }
    };

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl font-extrabold text-ink-900">Franchise manager</h1>
                <p className="mt-1 text-sm text-ink-500 max-w-2xl">
                    Manage franchise inventory by type. Entries appear on the landing page with their status (Available, Booked, Blocked).
                </p>
            </div>

            <div className="flex flex-wrap gap-2 border-b border-ink-200 pb-1">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`rounded-t-lg px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px ${
                            tab === t.id
                                ? 'border-brand-teal text-brand-teal-700 bg-brand-teal-50/50'
                                : 'border-transparent text-ink-500 hover:text-ink-800 hover:bg-ink-50'
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-bold text-ink-900">{activeTab.title}</h2>
                <div className="flex flex-wrap gap-3">
                    <input
                        className="ag-input w-52"
                        placeholder="Search franchises…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <select
                        className="ag-input w-40"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All status</option>
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,22rem)_1fr]">
                <form onSubmit={submit} className="ag-card space-y-4 h-fit xl:sticky xl:top-24">
                    <h3 className="font-bold text-ink-900">
                        {editingId ? 'Edit franchise' : 'Add new franchise'}
                    </h3>

                    {activeTab.fields.map((field) => (
                        <div key={field.key}>
                            <label className="ag-label">
                                {field.label}{field.required ? ' *' : ''}
                            </label>
                            {field.textarea ? (
                                <textarea
                                    className="ag-input min-h-[88px]"
                                    placeholder={field.placeholder}
                                    value={form[field.key] || ''}
                                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                                    required={field.required}
                                />
                            ) : (
                                <input
                                    className="ag-input"
                                    placeholder={field.placeholder}
                                    value={form[field.key] || ''}
                                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                                    required={field.required}
                                />
                            )}
                        </div>
                    ))}

                    <div>
                        <label className="ag-label">Status</label>
                        <select
                            className="ag-input"
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                        >
                            {STATUSES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="ag-label">Image</label>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="ag-input text-sm"
                            onChange={(e) => uploadImage(e.target.files?.[0])}
                            disabled={uploading}
                        />
                        <div className="mt-3 flex h-28 w-28 items-center justify-center overflow-hidden rounded-lg border border-ink-200 bg-ink-50">
                            {form.image ? (
                                <img src={uploadHref(form.image)} alt="Preview" className="h-full w-full object-cover" />
                            ) : (
                                <div className="text-center text-ink-400">
                                    <ImageIcon className="mx-auto h-6 w-6" />
                                    <span className="mt-1 block text-[10px]">Preview</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="ag-label">Description</label>
                        <textarea
                            className="ag-input min-h-[100px]"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Optional notes for internal or landing display"
                        />
                    </div>

                    <div className="flex gap-2 pt-1">
                        <button type="submit" className="ag-btn-primary flex-1" disabled={saving || uploading}>
                            {saving ? 'Saving…' : editingId ? 'Update franchise' : 'Add franchise'}
                        </button>
                        {editingId && (
                            <button type="button" className="ag-btn-ghost" onClick={resetForm}>
                                Cancel
                            </button>
                        )}
                    </div>
                </form>

                <div className="ag-card min-h-[24rem]">
                    <h3 className="mb-4 font-bold text-ink-900">
                        Franchises ({items.length})
                    </h3>

                    {loading && <p className="py-12 text-center text-ink-500">Loading…</p>}

                    {!loading && items.length === 0 && (
                        <p className="py-12 text-center text-ink-500 rounded-lg border border-dashed border-ink-200 bg-ink-50/50">
                            No franchises yet. Add one using the form.
                        </p>
                    )}

                    <div className="max-h-[calc(100vh-16rem)] space-y-3 overflow-y-auto pr-1">
                        {items.map((item) => (
                            <div
                                key={item._id}
                                className={`flex gap-4 rounded-xl border p-3 transition ${
                                    editingId === item._id
                                        ? 'border-brand-teal bg-brand-teal-50/30'
                                        : 'border-ink-100 bg-white hover:border-ink-200'
                                }`}
                            >
                                <div className="h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-ink-100">
                                    {item.image ? (
                                        <img
                                            src={uploadHref(item.image)}
                                            alt={item.displayName}
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <div className="grid h-full w-full place-items-center text-ink-300">
                                            <ImageIcon className="h-6 w-6" />
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-ink-900 truncate">{item.displayName}</p>
                                    <span className={`mt-1.5 inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold ring-1 ${STATUS_STYLES[item.status] || STATUS_STYLES.Available}`}>
                                        {item.status}
                                    </span>
                                    {item.description && (
                                        <p className="mt-2 line-clamp-2 text-xs text-ink-500">{item.description}</p>
                                    )}
                                </div>
                                <div className="flex shrink-0 flex-col gap-1 sm:flex-row sm:items-start">
                                    <button
                                        type="button"
                                        className="text-xs font-semibold text-brand-teal-700 hover:underline"
                                        onClick={() => startEdit(item)}
                                    >
                                        <span className="inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="text-xs font-semibold text-brand-red hover:underline"
                                        onClick={() => remove(item)}
                                    >
                                        <span className="inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
